/**
 * @file Tir automatique : cadence par chat, condition AutoFire / doigt pose, delegation au module de comportement de l'arme (registre).
 */
import type { SimContext } from '../Context';
import { isShootable } from '../combat/damage';
import { angleDiff, angleTo, DEG, dist } from '../math/geometry';
import { BEHAVIORS } from '../weapons/behaviors';
import type { System } from './System';

/** Ecart angulaire max entre la visee et la cible pour tirer. */
const FIRE_CONE = 12 * DEG;
/** Distance entre le centre du chat et la bouche du canon. */
const MUZZLE_OFFSET = 0.7;

/**
 * Pour chaque chat : cadence, reset de la dispersion, condition de tir (controle du mode, AutoFire ou doigt pose, cible valide
 * a portee et dans le cone de visee), puis delegation a BEHAVIORS.get(arme.behavior).fire. Au plus 1 tir par chat et par tic.
 */
export const WeaponSystem: System = {
  id: 'weapons',
  update(ctx: SimContext, dt: number): void {
    const team = ctx.services.team;
    const control = ctx.rt.control;
    const trigger = control.canFire && (control.autoFire || ctx.rt.input.active);
    for (const e of team.cats()) {
      const cat = e.cat;
      const weapon = team.resolveFor(cat);
      if (!weapon) continue;
      cat.cooldown -= dt;
      cat.sinceShot += dt;
      if (cat.sinceShot > weapon.params.spreadResetSeconds) cat.spreadDeg = weapon.params.spreadMinDeg;

      const target = trigger && cat.targetId !== null ? ctx.rt.world.get(cat.targetId) : undefined;
      let ready = false;
      if (target && isShootable(ctx, target)) {
        const d = dist(e.transform.x, e.transform.y, target.transform.x, target.transform.y);
        const a = angleTo(e.transform.x, e.transform.y, target.transform.x, target.transform.y);
        ready = d - target.collider.radius <= weapon.range && Math.abs(angleDiff(cat.aimAngle, a)) < FIRE_CONE;
      }
      if (ready && cat.cooldown <= 0 && target) {
        // Auto-aim : le corps pivote vers la cible (cat.aimAngle) ; la direction du tir est recalculee depuis la bouche du canon
        // vers la cible, les armes (dont les faisceaux instantanes) touchent donc ce qu'elles visent.
        const originX = e.transform.x + Math.cos(cat.aimAngle) * MUZZLE_OFFSET;
        const originY = e.transform.y + Math.sin(cat.aimAngle) * MUZZLE_OFFSET;
        BEHAVIORS.get(weapon.def.behavior).fire(ctx, {
          cat: e,
          weapon,
          originX,
          originY,
          aimAngle: angleTo(originX, originY, target.transform.x, target.transform.y),
          target,
        });
        cat.cooldown += weapon.interval;
        cat.sinceShot = 0;
      } else if (cat.cooldown < 0) {
        cat.cooldown = 0;
      }
    }
  },
};
